import { Themed } from "@client/styles/themes";
import React from "react";
import { VisuallyHidden } from "react-aria";
import { t } from "ttag";

import {
    FeaturedReview as ReviewValue,
    FeaturedReviewsBlock as Value,
} from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { Carousel } from "../GoodcookCarousel";
import { RichText } from "../RichText";
import { Svg } from "../Svg";
import { cleanCitation, cleanQuote } from "../TestimonialCardsBlock";

import styles from "./index.module.scss";

const StarRating = ({ rating }: { rating: number }) => {
    return (
        <div className={styles.rating}>
            <VisuallyHidden>{rating} out of 5 stars</VisuallyHidden>
            {Array.from({ length: 5 }, (_, i) => {
                const isFilled = i < rating;
                return (
                    <Svg
                        key={i}
                        name="star"
                        className={
                            isFilled ? styles.starFilled : styles.starEmpty
                        }
                        visuallyHiddenText={null}
                        aria-hidden="true"
                    />
                );
            })}
        </div>
    );
};

const FeaturedReview = ({ review }: { review: ReviewValue }) => (
    <Themed theme="white" className={styles.reviewCardWrapper}>
        <figure className={styles.reviewCard}>
            <div className={styles.reviewContent}>
                {review.rating && <StarRating rating={review.rating} />}

                {review.heading && (
                    <h3 className={styles.quoteHeading}>{review.heading}</h3>
                )}

                {review.quote && (
                    <blockquote className={styles.quote}>
                        {cleanQuote(review.quote)}
                    </blockquote>
                )}

                {review.citation && (
                    <figcaption>
                        <p className={styles.citation}>
                            {cleanCitation(review.citation)}
                        </p>
                    </figcaption>
                )}
            </div>

            {review.image && (
                <ImageChooserBlock
                    value={review.image}
                    sizes="(max-width: 767px) 104px, 200px"
                    attrs={{ className: styles.imageWrapper }}
                    imageAttrs={{ className: styles.image }}
                />
            )}
        </figure>
    </Themed>
);

interface Props {
    value: Value;
    id?: string;
}

export const FeaturedReviewsBlock = ({ value, id }: Props) => {
    if (!value.reviews || value.reviews.length === 0) return <></>;

    return (
        <div className={styles.root} id={id}>
            {value.heading && (
                <RichText value={value.heading} className={styles.heading} />
            )}

            <Carousel
                label={value.block_label || t`Featured reviews`}
                containerProps={{ className: styles.carouselContainer }}
                scrollContainerProps={{ className: styles.carousel }}
                advanceBy="item"
            >
                {value.reviews.map((review, index) => (
                    <FeaturedReview
                        key={`review-${id}-${index}`}
                        review={review}
                    />
                ))}
            </Carousel>
        </div>
    );
};
